/* eslint-disable @next/next/no-img-element */
import styled from 'styled-components'
import theme, { PrimaryColors } from 'stories/utils/theme'
import { getMobileOS } from 'lib/tracking/attribution/device'
import { Body2, ButtonText, Headline5 } from 'stories/elements/Typography/Text'
import _useTranslation from 'lib/hooks/_useTranslation'
import InformationDialog from 'stories/elements/Modal/InformationDialog'
import DownloadBanner from 'components/modules/SEO/DownloadBanner'
import ConversionImage from 'assets/know/webapp/conversion.png'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'
import { NewButtonLink } from 'components/elements/NewButton'
import { appKnowRoute, signUpRoute } from 'lib/constants/routes'
import { Know } from 'interfaces/Know'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { isDeviceMobile } from 'lib/features/deviceInfo'
import OpenTheAppDownloadButton from 'components/modules/SEO/OpenTheAppDownloadButton'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    gap: 20px;
    .texts {
        display: grid;
        gap: 5px;
        z-index: 1;
    }
`

const DownloadButtons = styled.div`
    position: relative;
    left: 0;
    bottom: 0px;
    z-index: 9;
    display: flex;
    gap: 10px;
    a {
        width: 100%;
    }
    @media (max-width: 750px) {
        justify-content: center;
    }
`

const ExplanationImage = styled.img`
    border-radius: ${theme.borderRadius.normal};
    object-position: bottom;
    position: relative;
    top: 115px;
    object-fit: cover;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 10px 15px;
`

interface Props {
    open: boolean
    handleClose: () => void
    title: string
    subtitle?: string
    onButtonClick(type: 'android' | 'ios'): void
    downloadDeepLinkPath?: string
    referrerScreen?: string
    know?: Know
    redirect?: string
    showOpenAppButton?: boolean
}

const WebappConversionDialog = (props: Props) => {
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')
    const mobileOS = getMobileOS() ?? 'ios'
    const md = useMedia(deviceSize.md)
    const isMobile = isDeviceMobile()
    const conversionDialogReferrer = props?.referrerScreen ?? 'know-conversion-dialog'
    const showMobileButton = mobileOS && md
    const authenticatedUser = useSelector((state: AppState) => state.user)

    const showKnowBackground = false //props.know && props.changeBackground

    const showWebappConversionEntryPoint = !authenticatedUser && !isMobile

    return (
        <InformationDialog
            open={props.open}
            onClose={props.handleClose}
            title={tCommon('common/openKnowunity')}
            ariaPrefix="know-conversion-dialog"
            sectionColor={showKnowBackground ? PrimaryColors.blueIce : PrimaryColors.yellowBackground}
            image={
                showKnowBackground ? (
                    <ExplanationImage src={props.know?.thumbnailLargeUrl} width={250} height={350} />
                ) : (
                    <img
                        src={ConversionImage.src}
                        width={ConversionImage.width}
                        height={ConversionImage.height}
                        alt="in-app screenshot"
                    />
                )
            }
        >
            <Container>
                <div className="texts">
                    <Headline5 fontFamily="var(--font-poppins)" fontWeight={600} color={theme.colors.headlineDark}>
                        {showWebappConversionEntryPoint ? t('knows/knowDetailsConversionDialogSignUpText') : props.title}
                    </Headline5>
                    <Body2 fontFamily="var(--font-inter)" fontWeight={400}>
                        {props.subtitle ??
                            t('knows/knowDetailsConversionDialogText', {
                                knowCount: KnowunityNumbers.knowThousandsCount,
                                userCount: KnowunityNumbers.downloadMillionCount,
                            })}
                    </Body2>
                </div>
                <DownloadButtons>
                    {showWebappConversionEntryPoint ? (
                        <NewButtonLink
                            borderRadius={theme.borderRadius.normal}
                            fullWidth
                            color={theme.colors.deepSeaBlue}
                            href={signUpRoute({
                                referrerScreen: conversionDialogReferrer,
                                redirect: props.redirect
                                    ? props.redirect
                                    : props?.know
                                    ? appKnowRoute(props.know.uuid, { referrerScreen: 'seoKnow' })
                                    : undefined,
                            })}
                        >
                            <ButtonText color={theme.colors.white}>
                                {t('knows/knowDetailsConversionDialogSignUpButtonText')}
                            </ButtonText>
                        </NewButtonLink>
                    ) : (
                        <>
                            {props.showOpenAppButton ? (
                                <OpenTheAppDownloadButton
                                    onClick={() => props.onButtonClick(mobileOS)}
                                    downloadDeepLinkPath={props.downloadDeepLinkPath}
                                    backgroundColor={theme.colors.headlineDark}
                                    isDark
                                    referrerScreen={conversionDialogReferrer}
                                />
                            ) : (
                                <>
                                    {showMobileButton ? (
                                        <DownloadBanner
                                            type={mobileOS}
                                            isDark
                                            onClick={() => props.onButtonClick(mobileOS)}
                                            downloadDeepLinkPath={props.downloadDeepLinkPath}
                                            referrerScreen={conversionDialogReferrer}
                                        />
                                    ) : (
                                        <>
                                            <DownloadBanner
                                                type="android"
                                                isDark
                                                onClick={() => props.onButtonClick('android')}
                                                downloadDeepLinkPath={props.downloadDeepLinkPath}
                                                referrerScreen={conversionDialogReferrer}
                                            />

                                            <DownloadBanner
                                                type="ios"
                                                isDark
                                                onClick={() => props.onButtonClick('ios')}
                                                downloadDeepLinkPath={props.downloadDeepLinkPath}
                                                referrerScreen={conversionDialogReferrer}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </DownloadButtons>
            </Container>
        </InformationDialog>
    )
}

export default WebappConversionDialog
